import { useEffect, useState, useMemo, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  Table,
  TableColumn,
  Link,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import {
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  makeStyles,
  Typography,
  Tooltip,
} from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  filterBar: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  select: {
    minWidth: 200,
  },
  tagChip: {
    margin: 2,
    fontSize: '0.7rem',
  },
  envChip: {
    margin: 2,
    fontSize: '0.7rem',
    backgroundColor: '#e3f2fd',
    color: '#0d47a1',
  },
  depChip: {
    margin: 2,
    fontSize: '0.7rem',
    backgroundColor: '#f3e5f5',
    color: '#4a148c',
  },
}));

interface ServiceRow {
  name: string;
  title: string;
  owner: string;
  namespace: string;
  lifecycle: string;
  team: string;
  product: string;
  environments: string[];
  sourceLocation: string;
  dependsOn: string[];
  entity: Entity;
}

async function collectOrgTeamOwnerNames(
  catalogApi: { getEntities: Function },
  rootName: string,
): Promise<Set<string>> {
  const { items } = await catalogApi.getEntities({
    filter: {
      kind: 'Group',
      'metadata.annotations.geniussports.com/org-team': 'true',
    },
  });

  const byName = new Map<string, Entity>();
  for (const e of items) byName.set(e.metadata.name, e);

  const names = new Set<string>();
  const visited = new Set<string>();
  const queue = [rootName];

  while (queue.length > 0) {
    const name = queue.shift()!;
    if (visited.has(name)) continue;
    visited.add(name);

    const entity = byName.get(name);
    if (!entity) continue;

    names.add(name.toLowerCase());

    const childrenAnn =
      entity.metadata.annotations?.['geniussports.com/org-team-children'] ?? '';
    const children = childrenAnn
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    for (const child of children) queue.push(child);
  }

  return names;
}

function toRows(items: Entity[]): ServiceRow[] {
  return items.map(e => {
    const spec = (e as any).spec ?? {};
    const ann = e.metadata.annotations ?? {};
    const envStr = ann['geniussports.com/environments'] ?? '';
    const environments = envStr
      ? envStr
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean)
      : [];

    const dependsOn: string[] = (spec.dependsOn ?? []).map((d: string) =>
      d.replace(/^component:default\//, ''),
    );

    return {
      name: e.metadata.name,
      title: (e.metadata as any).title ?? e.metadata.name,
      owner: spec.owner ?? '',
      namespace: e.metadata.namespace ?? 'default',
      lifecycle: spec.lifecycle ?? '',
      team: ann['geniussports.com/team'] ?? '',
      product: ann['geniussports.com/product'] ?? '',
      environments,
      sourceLocation: ann['backstage.io/source-location'] ?? '',
      dependsOn,
      entity: e,
    };
  });
}

export const GroupServicesContent = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const { entity } = useEntity();

  const groupName = entity.metadata.name;

  const [services, setServices] = useState<ServiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedLifecycle, setSelectedLifecycle] = useState<string>('');

  const load = useCallback(async () => {
    try {
      setLoading(true);

      const ownerNames = await collectOrgTeamOwnerNames(catalogApi, groupName);

      const { items } = await catalogApi.getEntities({
        filter: { kind: 'Component', 'spec.type': 'service' },
      });

      const matched = items.filter((e: Entity) => {
        const owner = ((e as any).spec?.owner ?? '').toLowerCase();
        return owner !== 'unknown' && ownerNames.has(owner);
      });

      setServices(toRows(matched));
      setError(undefined);
    } catch (e: any) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [catalogApi, groupName]);

  useEffect(() => {
    load();
  }, [load]);

  const teamOptions = useMemo(() => {
    const set = new Set(services.map(r => r.team).filter(Boolean));
    return Array.from(set).sort();
  }, [services]);

  const lifecycleOptions = useMemo(() => {
    const set = new Set(services.map(r => r.lifecycle).filter(Boolean));
    return Array.from(set).sort();
  }, [services]);

  const filtered = useMemo(
    () =>
      services.filter(r => {
        if (selectedTeam && r.team !== selectedTeam) return false;
        if (selectedLifecycle && r.lifecycle !== selectedLifecycle)
          return false;
        return true;
      }),
    [services, selectedTeam, selectedLifecycle],
  );

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  const columns: TableColumn<ServiceRow>[] = [
    {
      title: 'Name',
      field: 'title',
      highlight: true,
      render: row => (
        <Link to={`/catalog/${row.namespace}/component/${row.name}`}>
          {row.title}
        </Link>
      ),
    },
    {
      title: 'Owner',
      field: 'owner',
    },
    {
      title: 'Team',
      field: 'team',
    },
    {
      title: 'Product',
      field: 'product',
    },
    {
      title: 'Lifecycle',
      field: 'lifecycle',
      render: row => (
        <Chip
          label={row.lifecycle || '—'}
          size="small"
          className={classes.tagChip}
        />
      ),
    },
    {
      title: 'Environments',
      sorting: false,
      render: row =>
        row.environments.length > 0 ? (
          <Tooltip title={row.environments.join(', ')}>
            <Chip
              label={`${row.environments.length} env(s)`}
              size="small"
              className={classes.envChip}
            />
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      title: 'Dependencies',
      sorting: false,
      render: row =>
        row.dependsOn.length > 0 ? (
          <Tooltip title={row.dependsOn.join(', ')}>
            <Chip
              label={`${row.dependsOn.length} dep(s)`}
              size="small"
              className={classes.depChip}
            />
          </Tooltip>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <>
      <Box className={classes.filterBar}>
        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel>Team</InputLabel>
          <Select
            value={selectedTeam}
            onChange={e => setSelectedTeam(e.target.value as string)}
            label="Team"
          >
            <MenuItem value="">All Teams ({services.length})</MenuItem>
            {teamOptions.map(t => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.select}>
          <InputLabel>Lifecycle</InputLabel>
          <Select
            value={selectedLifecycle}
            onChange={e => setSelectedLifecycle(e.target.value as string)}
            label="Lifecycle"
          >
            <MenuItem value="">All</MenuItem>
            {lifecycleOptions.map(l => (
              <MenuItem key={l} value={l}>
                {l}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="textSecondary">
          Showing services owned by{' '}
          <strong>{(entity.metadata as any).title ?? groupName}</strong> and
          subgroups
        </Typography>
      </Box>
      <Table
        title={`Services (${filtered.length})`}
        columns={columns}
        data={filtered}
        options={{
          search: true,
          paging: true,
          pageSize: 25,
          pageSizeOptions: [25, 50, 100],
          padding: 'dense',
        }}
      />
    </>
  );
};
